// src/modules/gyms/pages/BranchDetailPage.tsx
import { useParams, useNavigate } from "react-router-dom";
import { Alert, Row, Col } from "react-bootstrap";
import { useState, useMemo, useContext, useEffect } from "react";
import { message } from "antd";
import { useGymPartner } from "../contexts/gym.context";
import { useGymBrands } from "../hooks/useGymBrands";
import { BranchDetailHeader } from "../components/branch-page/BranchDetailHeader";
import { BranchAmenities } from "../components/branch-page/BranchAmenities";
import { BranchOpeningHours } from "../components/branch-page/BranchOpeningHours";
import { BranchFormModal } from "../components/BranchFormModal";
import { ConfirmModal } from "@modules/shared/components";
import { AuthContext } from "@modules/auth/contexts/auth-plus.context";
import { hasManagePermission } from "@modules/auth/utils/role.utils";
import { BranchDetailSkeleton } from "../components/skeleton";
import { ServiceList } from "../components/ServiceList";
import { ServiceFormModal } from "../components/ServiceFormModal";

export default function BranchDetailPage() {
  const { branchId } = useParams<{ branchId: string }>();
  const navigate = useNavigate();
  const { auth } = useContext(AuthContext);

  const {
    branches,
    services,
    loading,
    loaded,
    error,
    branchActions,
    serviceActions,
    refresh: refreshGymData,
    loadServicesForBrand,
  } = useGymPartner();
  const { brands } = useGymBrands();

  // Branch detail states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Service modal states
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState<any>(null);
  const [serviceViewMode, setServiceViewMode] = useState(false);
  const [deleteServiceTarget, setDeleteServiceTarget] = useState<{
    id: string;
    name: string;
    branchId: string;
  } | null>(null);

  const canManage = hasManagePermission(auth);
  const branch = branches.find((b) => b.id === branchId);
  const brand = branch ? brands.find((b) => b.id === branch.brandId) : null;
  const brandLogoUrl = brand?.logo?.url || "";

  // All services of this branch - no pagination here, ServiceList handles it
  const branchServices = useMemo(
    () => services.filter((s) => s.branchId === branchId),
    [services, branchId],
  );

  // Load services for brand if not loaded yet
  useEffect(() => {
    if (branch && branch.brandId) {
      loadServicesForBrand(branch.brandId, [branch]);
    }
  }, [branch, loadServicesForBrand]);

  const handleEdit = () => {
    setEditingBranch(branch);
    setShowEditModal(true);
  };

  const handleToggleStatus = async () => {
    if (!branch) return;
    try {
      await branchActions.updateBranch(branch.id, {
        isActive: !branch.isActive,
      });
      message.success(`Chi nhánh đã ${branch.isActive ? "đóng" : "mở"}`);
    } catch (err: any) {
      message.error(err.message || "Cập nhật trạng thái thất bại");
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!branch) return;
    try {
      await branchActions.deleteBranch(branch.id);
      message.success(`Chi nhánh "${branch.name}" đã được xóa`);
      setShowDeleteConfirm(false);
      navigate(`/b2b/gyms/${branch.brandId}`);
    } catch (err: any) {
      message.error(err.message || "Xóa thất bại");
    }
  };

  const handleSaveBranch = async (data: any, openingHoursOverride?: any) => {
    if (!branch) return;
    try {
      await branchActions.updateBranch(branch.id, data, openingHoursOverride);
      message.success("Chi nhánh đã được cập nhật");
      setShowEditModal(false);
      setEditingBranch(null);
    } catch (err: any) {
      message.error(err.message || "Cập nhật thất bại, vui lòng thử lại.");
    }
  };

  const handleSaveService = async (data: any) => {
    try {
      if (editingService) {
        await serviceActions.updateService(editingService.id, {
          ...data,
          branchId: branchId!,
        });
        message.success("Đã cập nhật dịch vụ");
      } else {
        await serviceActions.createService({
          ...data,
          branchId: branchId!,
          brandId: branch!.brandId,
        });
        message.success("Đã tạo dịch vụ");
      }
      await refreshGymData();
      if (branch) {
        await loadServicesForBrand(branch.brandId, [branch]);
      }
      setShowServiceModal(false);
      setEditingService(null);
      setServiceViewMode(false);
    } catch (err: any) {
      message.error(err.message || "Lưu dịch vụ thất bại");
    }
  };

  const handleDeleteService = async () => {
    if (!deleteServiceTarget) return;
    try {
      await serviceActions.deleteService(
        deleteServiceTarget.id,
        deleteServiceTarget.branchId,
      );
      message.success(`Đã xóa dịch vụ: ${deleteServiceTarget.name}`);
      await refreshGymData();
      if (branch) {
        await loadServicesForBrand(branch.brandId, [branch]);
      }
    } catch (err: any) {
      message.error(err.message || "Xóa dịch vụ thất bại");
    } finally {
      setDeleteServiceTarget(null);
    }
  };

  const handleToggleActive = async (service: any, active: boolean) => {
    try {
      await serviceActions.updateService(service.id, {
        branchId: service.branchId,
        brandId: service.brandId,
        active,
      });
      message.success(active ? "Đã bật dịch vụ" : "Đã tắt dịch vụ");
      await refreshGymData();
      if (branch) {
        await loadServicesForBrand(branch.brandId, [branch]);
      }
    } catch (err: any) {
      message.error(err.message || "Không cập nhật được trạng thái");
    }
  };

  if (!loaded && loading) {
    return <BranchDetailSkeleton />;
  }

  if (error) {
    return (
      <Alert variant="danger" className="mt-4">
        Đã xảy ra lỗi: {error}
      </Alert>
    );
  }

  if (!branch) {
    return (
      <div className="card border-0 shadow-sm mt-4">
        <div className="card-body text-center text-muted py-5">
          Không tìm thấy chi nhánh.
        </div>
      </div>
    );
  }

  const images = branch.images || [];
  const bannerUrl =
    images[0]?.url ||
    "https://placehold.co/1200x400/f0ede8/78716c?text=No+Banner";

  return (
    <div className="mt-4">
      <BranchDetailHeader
        name={branch.name}
        address={branch.address}
        bannerUrl={bannerUrl}
        logoUrl={
          brandLogoUrl || "https://placehold.co/80x80/f0ede8/78716c?text=Logo"
        }
        status={branch.isActive}
        operatingHoursFrom={branch.operatingHoursFrom}
        operatingHoursTo={branch.operatingHoursTo}
        openingHours={branch.openingHours}
        categories={branch.categories}
        description={branch.description}
        ctaLink={branch.ctaLink}
        updatedAt={branch.updatedAt}
        isActive={branch.isActive}
        onToggleStatus={handleToggleStatus}
        onEdit={handleEdit}
        onDelete={handleDelete}
        canManage={canManage}
      />

      <Row className="mt-4 g-3">
        <Col md={6} xs={12}>
          <BranchAmenities amenities={branch.amenities} />
        </Col>
        <Col md={6} xs={12}>
          <BranchOpeningHours
            openingHours={branch.openingHours}
            defaultFrom={branch.operatingHoursFrom}
            defaultTo={branch.operatingHoursTo}
          />
        </Col>
      </Row>

      {/* Dịch vụ của chi nhánh - ServiceList tự phân trang */}
      <div className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">Dịch vụ</h5>
          {canManage && (
            <button
              className="btn btn-sm btn-primary"
              onClick={() => {
                setEditingService(null);
                setServiceViewMode(false);
                setShowServiceModal(true);
              }}
            >
              Thêm dịch vụ
            </button>
          )}
        </div>
        <ServiceList
          services={branchServices}
          branches={[branch]}
          canManage={canManage}
          onView={(service) => {
            setEditingService(service);
            setServiceViewMode(true);
            setShowServiceModal(true);
          }}
          onDelete={(service) => {
            setDeleteServiceTarget({
              id: service.id,
              name: service.name,
              branchId: service.branchId,
            });
          }}
          onToggleActive={handleToggleActive}
          loading={loading}
        />
      </div>

      <BranchFormModal
        show={showEditModal}
        brandId={branch.brandId}
        editingBranch={editingBranch}
        onSave={handleSaveBranch}
        onClose={() => {
          setShowEditModal(false);
          setEditingBranch(null);
        }}
      />

      <ServiceFormModal
        show={showServiceModal}
        brandId={branch.brandId}
        branchOptions={[{ id: branch.id, name: branch.name }]}
        editingService={editingService}
        startInViewMode={serviceViewMode}
        defaultBranchId={branch.id}
        onSave={handleSaveService}
        onClose={() => {
          setShowServiceModal(false);
          setEditingService(null);
          setServiceViewMode(false);
        }}
      />

      <ConfirmModal
        show={showDeleteConfirm}
        title="Xóa chi nhánh"
        message={`Bạn có chắc chắn muốn xóa chi nhánh "${branch.name}"?`}
        confirmText="Xóa"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <ConfirmModal
        show={!!deleteServiceTarget}
        title="Xóa dịch vụ"
        message={`Bạn có chắc chắn muốn xóa dịch vụ "${deleteServiceTarget?.name}"?`}
        confirmText="Xóa"
        variant="danger"
        onConfirm={handleDeleteService}
        onCancel={() => setDeleteServiceTarget(null)}
      />
    </div>
  );
}
